import React, { createContext, useContext, useReducer, useMemo } from 'react';

// Checkout state management
const CheckoutContext = createContext();

const initialState = {
    // Current step in checkout process
    currentStep: 1,
    totalSteps: 4,

    // Order information
    order: null,
    orderId: null,
    orderNumber: null,
    expiresAt: null,

    // Customer information
    customerInfo: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        agreeToTerms: false,
        subscribeToNewsletter: false
    },

    // Billing information
    billingInfo: {
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'US'
    },

    // Payment information
    paymentInfo: {
        method: 'card', // 'card', 'paypal', 'apple_pay', etc.
        cardLast4: null,
        cardBrand: null
    },

    // Processing states
    isLoading: false,
    isValidating: false,
    isProcessingPayment: false,

    // Error handling
    errors: {},
    generalError: null,

    // Validation states
    validatedSteps: new Set(),

    // Timer for reservation expiration
    timeRemaining: 0
};

function checkoutReducer(state, action) {
    switch (action.type) {
        case 'SET_LOADING':
            return { ...state, isLoading: action.payload };

        case 'SET_VALIDATING':
            return { ...state, isValidating: action.payload };

        case 'SET_PROCESSING_PAYMENT':
            return { ...state, isProcessingPayment: action.payload };

        case 'SET_STEP':
            return { ...state, currentStep: action.payload };

        case 'NEXT_STEP':
            return {
                ...state,
                currentStep: Math.min(state.currentStep + 1, state.totalSteps),
                validatedSteps: new Set([...state.validatedSteps, state.currentStep])
            };

        case 'PREV_STEP':
            return { ...state, currentStep: Math.max(state.currentStep - 1, 1) };

        case 'SET_ORDER':
            return {
                ...state,
                order: action.payload.order,
                orderId: action.payload.order?.id,
                orderNumber: action.payload.order?.orderNumber,
                expiresAt: action.payload.expiresAt
            };

        case 'UPDATE_CUSTOMER_INFO':
            return {
                ...state,
                customerInfo: { ...state.customerInfo, ...action.payload }
            };

        case 'UPDATE_BILLING_INFO':
            return {
                ...state,
                billingInfo: { ...state.billingInfo, ...action.payload }
            };

        case 'UPDATE_PAYMENT_INFO':
            return {
                ...state,
                paymentInfo: { ...state.paymentInfo, ...action.payload }
            };

        case 'SET_ERRORS':
            return { ...state, errors: action.payload };

        case 'ADD_ERROR':
            return {
                ...state,
                errors: { ...state.errors, [action.field]: action.message }
            };

        case 'CLEAR_ERROR':
            const newErrors = { ...state.errors };
            delete newErrors[action.field];
            return { ...state, errors: newErrors };

        case 'SET_GENERAL_ERROR':
            return { ...state, generalError: action.payload };

        case 'CLEAR_GENERAL_ERROR':
            return { ...state, generalError: null };

        case 'SET_TIME_REMAINING':
            return { ...state, timeRemaining: action.payload };

        case 'VALIDATE_STEP':
            return {
                ...state,
                validatedSteps: new Set([...state.validatedSteps, action.payload])
            };

        case 'RESET_CHECKOUT':
            return { ...initialState };

        default:
            return state;
    }
}

export const CheckoutProvider = ({ children }) => {
    const [state, dispatch] = useReducer(checkoutReducer, initialState);

    const actions = useMemo(() => ({
        setLoading: (loading) => dispatch({ type: 'SET_LOADING', payload: loading }),
        setValidating: (validating) => dispatch({ type: 'SET_VALIDATING', payload: validating }),
        setProcessingPayment: (processing) => dispatch({ type: 'SET_PROCESSING_PAYMENT', payload: processing }),
        setStep: (step) => dispatch({ type: 'SET_STEP', payload: step }),
        nextStep: () => dispatch({ type: 'NEXT_STEP' }),
        prevStep: () => dispatch({ type: 'PREV_STEP' }),
        setOrder: (order, expiresAt) => dispatch({ type: 'SET_ORDER', payload: { order, expiresAt } }),
        updateCustomerInfo: (info) => dispatch({ type: 'UPDATE_CUSTOMER_INFO', payload: info }),
        updateBillingInfo: (info) => dispatch({ type: 'UPDATE_BILLING_INFO', payload: info }),
        updatePaymentInfo: (info) => dispatch({ type: 'UPDATE_PAYMENT_INFO', payload: info }),
        setErrors: (errors) => dispatch({ type: 'SET_ERRORS', payload: errors }),
        addError: (field, message) => dispatch({ type: 'ADD_ERROR', field, message }),
        clearError: (field) => dispatch({ type: 'CLEAR_ERROR', field }),
        setGeneralError: (error) => dispatch({ type: 'SET_GENERAL_ERROR', payload: error }),
        clearGeneralError: () => dispatch({ type: 'CLEAR_GENERAL_ERROR' }),
        setTimeRemaining: (time) => dispatch({ type: 'SET_TIME_REMAINING', payload: time }),
        validateStep: (step) => dispatch({ type: 'VALIDATE_STEP', payload: step }),
        resetCheckout: () => dispatch({ type: 'RESET_CHECKOUT' })
    }), [dispatch]);

    return (
        <CheckoutContext.Provider value={{ state, actions }}>
            {children}
        </CheckoutContext.Provider>
    );
};

export const useCheckout = () => {
    const context = useContext(CheckoutContext);
    if (!context) {
        throw new Error('useCheckout must be used within a CheckoutProvider');
    }
    return context;
};

export default CheckoutContext;
